import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * The table the console is mostly made of.
 *
 * Square corners and a sticky header, sitting inside a `Card` with
 * `padded={false}` so rows meet the panel edge. Row separation is a hairline
 * in `border-2` — a paler line than the panel's own border, so the outer
 * edge still reads as the edge and the interior rules recede.
 *
 * `overflow-x-auto` on the wrapper rather than a horizontal scrollbar on
 * the page: a wide café table should scroll inside its panel, never take
 * the whole layout sideways with it.
 */
export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="overflow-x-auto">{children}</div>;
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <table className="w-full border-collapse text-[12.5px]">{children}</table>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="sticky top-0 z-10 bg-accent-soft/80 backdrop-blur-sm">
      {children}
    </thead>
  );
}

export function TH({
  children,
  align = "left",
  className,
}: {
  children?: ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "eyebrow border-b border-border px-3 py-2.5 font-bold text-accent",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TR({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        "border-b border-border-2 last:border-b-0",
        onClick && "cursor-pointer transition-colors hover:bg-accent-soft/45",
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  align = "left",
  mono,
  muted,
  className,
}: {
  children?: ReactNode;
  align?: "left" | "right" | "center";
  /** Numbers, ids and timestamps. Tabular by default — see index.css. */
  mono?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <td
      className={cn(
        "px-3 py-[7px] align-middle",
        align === "right" && "text-right",
        align === "center" && "text-center",
        mono && "font-mono text-[11.5px]",
        muted && "text-muted",
        className,
      )}
    >
      {children}
    </td>
  );
}
